import React from 'react';
import type { LearningPlanRecord } from '../../../../services/learningPlansService';

type LearningPlansPageProps = {
  plans: LearningPlanRecord[];
  isLoading: boolean;
  error?: string | null;
  onBack?: () => void;
  onOpenPlan?: (plan: LearningPlanRecord) => void;
};

const LearningPlansPage: React.FC<LearningPlansPageProps> = ({ plans, isLoading, error, onBack, onOpenPlan }) => {
  return (
    <div className="bg-white rounded-lg shadow h-full overflow-auto">
      <div className="border-b border-gray-200 py-6 px-6 bg-white">
        <h1 className="text-2xl font-semibold text-gray-800 text-center">Learning Plans</h1>
        {onBack && (
          <div className="mt-3 text-center">
            <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-800">Back</button>
          </div>
        )}
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="text-sm text-gray-600">Loading learning plans…</div>
        ) : error ? (
          <div className="text-sm text-red-700 font-semibold">{error}</div>
        ) : plans.length === 0 ? (
          <div className="text-sm text-gray-600">No learning plans saved yet.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {plans.map((p) => {
              const title = p?.title || p?.plan?.title || 'Learning Plan';
              const when = p?.accepted_at || p?.created_at;
              return (
                <div
                  key={p.plan_id}
                  className="bg-white rounded-lg shadow-sm border p-4 hover:shadow transition-shadow flex flex-row items-stretch gap-4 relative cursor-pointer"
                  onClick={() => onOpenPlan?.(p)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onOpenPlan?.(p);
                  }}
                  title="Open learning plan"
                >
                  {/* Placeholder image block (style copied from simulations cards) */}
                  <div className="w-40 h-32 rounded overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    <div className="w-full h-full flex items-center justify-center" style={{
                      background: 'linear-gradient(135deg, rgba(1,188,212,0.18) 0%, rgba(132,51,255,0.14) 60%, rgba(4,36,52,0.10) 100%)'
                    }}>
                      <div className="text-gray-700 font-semibold">LP</div>
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col py-1 gap-1">
                    <div className="text-base font-semibold text-gray-900">{title}</div>
                    {p?.plan?.estimated_time_to_complete || p?.plan?.estimated_schedule ? (
                      <div className="text-sm text-gray-600">
                        {p?.plan?.estimated_time_to_complete ? <span>{p.plan.estimated_time_to_complete}</span> : null}
                        {p?.plan?.estimated_time_to_complete && p?.plan?.estimated_schedule ? <span> • </span> : null}
                        {p?.plan?.estimated_schedule ? <span>{p.plan.estimated_schedule}</span> : null}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">Saved learning plan</div>
                    )}
                    {when ? (
                      <div className="text-xs text-gray-500">Saved: {new Date(when).toLocaleString()}</div>
                    ) : null}
                  </div>

                  <div className="flex flex-col items-end justify-end gap-2">
                    <button
                      className="bg-[#25394D] hover:brightness-110 text-white py-2 px-3 rounded-md text-sm"
                      onClick={(e) => { e.stopPropagation(); onOpenPlan?.(p); }}
                    >
                      Open
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningPlansPage;


